<script lang="ts">
	import { positions, positionsToShow, currentMarket } from '../store';
	import UserPosition from './UserPosition/UserPosition.svelte';
	import { smartWalletCreationPending, walletConnected, walletLoaded } from '$lib/wallet/store';
	import { smartContractWalletAddress } from '../scwallet/store';
	import Tooltip from '$lib/tooltip/Tooltip.svelte';
	import { fly } from 'svelte/transition';
	import { quadIn } from 'svelte/easing';
	import { fullyCloseAllPositions } from '../userInteract/userInteract';
	import { addDollarSign, getNumBackInUnits } from '$lib/util/util';
	import { marketsConfig } from '$lib/config/markets';
	import BigNumber from 'bignumber.js';
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import { getDisplayName, getTokenLogo } from '$lib/perps';
	import { page } from '$app/stores';

	export let showClosePosition = true;

	$: skewRate =
		$currentMarket && $currentMarket.longOI && $currentMarket.shortOI
			? new BigNumber($currentMarket.longOI)
					.div(new BigNumber($currentMarket.shortOI).plus($currentMarket.longOI))
					.toNumber()
			: 0.5;
	$: skew = $currentMarket
		? new BigNumber($currentMarket.longOI).minus($currentMarket.shortOI)
		: new BigNumber(0);
	$: discountOrPremium = $currentMarket
		? new BigNumber(skew).div($currentMarket.skewScale)
		: new BigNumber(0);

	const closeAllPositions = () => {
		if ($positions.data) {
			fullyCloseAllPositions($positions.data);
		}
	};
</script>

{#if $positionsToShow && $positionsToShow.length > 0}
	<div
		class="hidden sm:grid px-2 text-fx-grey-400 {showClosePosition
			? 'sm:grid-cols-10'
			: 'grid-cols-9'}"
	>
		<div class="text-xs">Perp</div>
		<div class="text-xs text-center">Side</div>
		<div class="text-xs text-center">Size</div>
		<div class="text-xs text-center">Avg. Entry</div>
		<div class="text-xs text-center">Liq. Price</div>
		<div class="text-xs text-center cursor-help">
			<Tooltip
				header="Profit and Loss"
				title="The profit or loss of the position, includes accrued funding.">PnL</Tooltip
			>
		</div>
		<div class="text-xs text-center cursor-help">
			<Tooltip header="Accrued Funding" title="Accumulated funding since the opening of position."
				>Funding</Tooltip
			>
		</div>
		<div class="text-xs text-center">Margin</div>
		<div class="text-xs text-center cursor-help">
			<Tooltip header="Take Profit/Stop Loss" title="Take Profit/Stop Loss orders">TP/SL</Tooltip>
		</div>

		{#if showClosePosition}
			<button
				class="text-xs text-center text-fx-yellow cursor-pointer underline"
				on:click={closeAllPositions}
				on:keydown={closeAllPositions}
			>
				Close all
			</button>
		{/if}
	</div>
{/if}
<div class="overflow-auto no-scrollbar sm:max-h-[30vh]">
	{#if $walletLoaded && $walletConnected && $positionsToShow && $positionsToShow.length > 0}
		<div class="sm:mt-6">
			<div class="flex flex-col overflow-y">
				{#each $positionsToShow as position, i}
					<div
						in:fly={{
							duration: 300 * Math.sqrt(i),
							x: 0,
							y: 30,
							opacity: 0.1,
							easing: quadIn
						}}
					>
						<UserPosition {position} {showClosePosition} />
					</div>
				{/each}
			</div>
		</div>
	{:else}
		<div class="py-10 flex-col items-center justify-center">
			{#if $positions.loading && $walletLoaded && $walletConnected && $smartContractWalletAddress}
				<div class="grid gap-2">
					<div
						class="w-full h-12 bgx-fx-grey-600 dark:bg-fx-grey-700 animate-pulse rounded-xl squircle-xl"
					/>
					<div
						class="w-full h-12 bgx-fx-grey-600 dark:bg-fx-grey-700 animate-pulse rounded-xl squircle-xl"
					/>
					<div
						class="w-full h-12 bgx-fx-grey-600 dark:bg-fx-grey-700 animate-pulse rounded-xl squircle-xl"
					/>
				</div>
			{:else if $walletLoaded && $walletConnected && $smartContractWalletAddress}
				<img loading="lazy" src={'empty-position.png'} alt="" class="mx-auto max-w-[30%] mb-3" />
				<div class="text-center text-fx-grey-200 font-medium text-sm mb-2">
					No positions open yet.
				</div>
				<div class="w-full text-fx-grey-400 text-xs rounded-xl squircle-xl text-center">
					Once you place an order, you can see your open positions here. Place order
					<a href="/perps" class="text-fx-pink">here.</a>
				</div>
			{:else if $walletLoaded && !$walletConnected}
				<img loading="lazy" src={'empty-position.png'} alt="" class="mx-auto max-w-[30%] mb-3" />
				<div class="text-center text-fx-grey-200 font-medium text-sm mb-2">Connect wallet</div>
				<div class="w-full text-fx-grey-400 text-xs rounded-xl squircle-xl text-center">
					Once you place an order, you can see your open positions here. Place order .
					<a href="/perps" class="text-fx-pink">here.</a>
				</div>
			{:else if $smartWalletCreationPending}
				<img loading="lazy" src={'empty-position.png'} alt="" class="mx-auto max-w-[30%] mb-3" />
				<div class="text-center text-fx-grey-200 font-medium text-sm mb-2">
					Create Smart Contract Wallet
				</div>
				<div class="w-full text-fx-grey-400 text-xs rounded-xl squircle-xl text-center">
					Once you place an order, you can see your open positions here. Place order .
					<a href="/perps" class="text-fx-pink">here.</a>
				</div>
			{:else}
				<div class="grid gap-2">
					<div
						class="w-full h-12 bgx-fx-grey-600 dark:bg-fx-grey-700 animate-pulse rounded-xl squircle-xl"
					/>
					<div
						class="w-full h-12 bgx-fx-grey-600 dark:bg-fx-grey-700 animate-pulse rounded-xl squircle-xl"
					/>
					<div
						class="w-full h-12 bgx-fx-grey-600 dark:bg-fx-grey-700 animate-pulse rounded-xl squircle-xl"
					/>
				</div>
			{/if}
		</div>
	{/if}
</div>

{#if $page.url.pathname === '/perps'}
	{#if $currentMarket}
		<GradientBorder
			gradient="bg-fx-grey-500"
			paddingClass="p-px"
			borderRadius="rounded-xl squircle-xl"
			outerClassList="w-full h-fit my-5 sm:hidden"
			innerClassList="flex justify-center items-center bg-fx-black w-full"
		>
			<div
				class="p-4 sm:p-6 flex flex-col items-start w-full rounded-xl squircle-xl bg-fx-gradient-8-40"
			>
				<div
					class="grid grid-cols-2 gap-4 sm:grid-cols-11 items-start w-full cursor-pointer whitespace-nowrap"
				>
					<div class="flex gap-2">
						<img
							loading="lazy"
							src={getTokenLogo($currentMarket.marketIdentifier)}
							alt="logo"
							class="w-5 h-5 rounded-full"
						/>
						<div class="text-fx-grey-100 text-sm rounded-lg squircle-lg font-medium">
							{getDisplayName($currentMarket.marketIdentifier)}
						</div>
					</div>

					<div />

					<div class="text-fx-grey-100 text-sm font-medium sm:text-right">
						<div class="py-2 sm:hidden text-fx-grey-300 text-xs">Market Price</div>
						{addDollarSign(
							getNumBackInUnits(
								$currentMarket.currentMarketPrice,
								18,
								marketsConfig[$currentMarket.asset].showDecimals
							),
							marketsConfig[$currentMarket.asset].showDecimals
						)}
					</div>

					<div class="text-fx-grey-100 text-sm font-medium sm:text-right">
						<div class="py-2 sm:hidden text-fx-grey-300 text-xs">Index Price</div>
						{addDollarSign(
							getNumBackInUnits(
								$currentMarket.currentOraclePrice,
								18,
								marketsConfig[$currentMarket.asset].showDecimals
							),
							marketsConfig[$currentMarket.asset].showDecimals
						)}
					</div>
					<div class="text-sm font-medium sm:text-right">
						<div class="py-2 sm:hidden text-fx-grey-300 text-xs">Funding 1 hour</div>
						<div class="flex flex-col">
							<div
								class:text-fx-green={new BigNumber($currentMarket.fundingRatePercentageLast1h).gte(
									0
								)}
								class:text-fx-red={new BigNumber($currentMarket.fundingRatePercentageLast1h).lte(0)}
							>
								{getNumBackInUnits($currentMarket.fundingRatePercentageLast1h, 18, 4)}%
							</div>
							<div class="text-gray-500 text-xs">
								{getNumBackInUnits($currentMarket.fundingVelocity, 18, 4)}%
							</div>
						</div>
					</div>
					<div
						class="text-sm font-medium sm:text-right"
						class:text-fx-green={new BigNumber($currentMarket.fundingRatePercentageLast1h).gte(0)}
						class:text-fx-red={new BigNumber($currentMarket.fundingRatePercentageLast1h).lte(0)}
					>
						<div class="py-2 sm:hidden text-fx-grey-300 text-xs">Funding APR</div>

						{(
							Number(getNumBackInUnits($currentMarket.fundingRatePercentageLast1h, 18, 4)) *
							24 *
							365
						).toFixed(2)}%
					</div>

					<div class="text-fx-grey-100 text-sm font-medium sm:text-right">
						<div class="py-2 sm:hidden text-fx-grey-300 text-xs">Open Interest</div>

						{addDollarSign(getNumBackInUnits($currentMarket.openInterest, 18, 2), 2)}
					</div>
					<div class="text-fx-grey-100 text-sm font-medium sm:text-right">
						<div class="py-2 sm:hidden text-fx-grey-300 text-xs">Max Market Value</div>

						{addDollarSign($currentMarket.maxMarketValueUSD, 2)}
					</div>
					<div>
						<div class="text-fx-grey-100 text-sm font-medium sm:text-right">
							<div class="py-2 sm:hidden text-fx-grey-300 text-xs">Long OI</div>

							{addDollarSign($currentMarket.longOIUSD, 2)}
						</div>
						<div class="text-fx-grey-300 text-xs font-medium sm:text-right">
							<div class="py-2 sm:hidden text-fx-grey-300 text-xs">Long OI left</div>

							{addDollarSign(
								parseFloat($currentMarket.maxMarketValueUSD) - parseFloat($currentMarket.longOIUSD),
								2
							)}
						</div>
					</div>
					<div>
						<div class="text-fx-grey-100 text-sm font-medium sm:text-right">
							<div class="py-2 sm:hidden text-fx-grey-300 text-xs">Short OI</div>

							{addDollarSign($currentMarket.shortOIUSD, 2)}
						</div>
						<div class="text-fx-grey-300 text-xs font-medium sm:text-right">
							<div class="py-2 sm:hidden text-fx-grey-300 text-xs">Short OI left</div>
							{addDollarSign(
								parseFloat($currentMarket.maxMarketValueUSD) -
									parseFloat($currentMarket.shortOIUSD),
								2
							)}
						</div>
					</div>

					<div class="text-fx-grey-100 text-sm font-medium sm:text-right">
						<div class="py-2 sm:hidden text-fx-grey-300 text-xs">Skew</div>

						<span class="text-fx-green">
							{isNaN(skewRate) ? 0 : (100 * skewRate).toFixed(2)}%
						</span>
						<span class="text-fx-grey-400">/</span>
						<span class="text-fx-red">
							{isNaN(skewRate) ? 0 : (100 * (1 - skewRate)).toFixed(2)}%
						</span>
					</div>

					<div class="text-fx-grey-100 text-sm font-medium sm:text-right">
						<div class="py-2 sm:hidden text-fx-grey-300 text-xs">Discount/Premium</div>

						<div class="flex flex-col">
							<div class={discountOrPremium.gt(0) ? 'text-green-500' : 'text-red-500'}>
								{getNumBackInUnits(discountOrPremium.multipliedBy(1e20).toString(), 18, 4)}
							</div>
							<div class="text-xs text-gray-500">
								{getNumBackInUnits(discountOrPremium.multipliedBy(1e22).toString(), 18, 4)}bp
							</div>
						</div>
					</div>
				</div>
			</div>
		</GradientBorder>
	{:else}
		<GradientBorder
			gradient="bg-fx-grey-500"
			paddingClass="p-px"
			borderRadius="rounded-xl squircle-xl"
			outerClassList="w-full h-fit my-5 sm:hidden"
			innerClassList="flex justify-center items-center bg-fx-black w-full"
		>
			<div
				class="bg-fx-gradient-8-50 grid grid-cols-2 sm:grid-cols-10 items-center py-2 sm:py-3 px-2 sm:px-5 w-full rounded-lg squircle-lg gap-2"
			>
				<div class="animate-pulse bg-fx-grey-800 h-9 w-full col-span-2 rounded-lg squircle-lg" />
				<div class="animate-pulse bg-fx-grey-800 h-9 w-full col-span-2 rounded-lg squircle-lg" />
				<div class="animate-pulse bg-fx-grey-800 h-9 w-full col-span-2 rounded-lg squircle-lg" />
				<div class="animate-pulse bg-fx-grey-800 h-9 w-full col-span-2 rounded-lg squircle-lg" />
				<div class="animate-pulse bg-fx-grey-800 h-9 w-full col-span-2 rounded-lg squircle-lg" />
				<div class="animate-pulse bg-fx-grey-800 h-9 w-full col-span-2 rounded-lg squircle-lg" />
			</div>
		</GradientBorder>
	{/if}
{/if}
